import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';

interface PasswordResetProps {
  setView?: (view: string) => void;
}

const PasswordReset = ({ setView }: PasswordResetProps) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // Use the current origin and include the proper auth page path
      const currentOrigin = window.location.origin;
      const redirectUrl = `${currentOrigin}/auth?view=update-password&recovery=true`;
      console.log("Setting password reset redirect to:", redirectUrl);
      
      const { error, data } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) {
        throw error;
      }
      
      setSubmitted(true);
      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link.",
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error resetting password",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    if (setView) {
      setView('login');
    } else {
      navigate('/auth');
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto border-0 shadow-sm">
      <CardHeader>
        <CardTitle>{submitted ? "Check Your Email" : "Reset Password"}</CardTitle>
        <CardDescription>
          {submitted 
            ? "We've sent a password reset link to your email address."
            : "Enter your email address and we'll send you a link to reset your password."}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {!submitted ? (
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="grid w-full items-center gap-2">
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="pl-10"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        ) : (
          <div className="text-center p-4 space-y-4">
            <Mail className="mx-auto h-12 w-12 text-primary" />
            <p>
              If an account exists with the email <strong>{email}</strong>,
              we've sent a password reset link.
            </p>
          </div>
        )}
      </CardContent>
      
      <CardFooter>
        <Button 
          variant="ghost" 
          className="w-full flex items-center gap-2" 
          onClick={handleBackToLogin}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PasswordReset;
